"use-client";

// Import necessary dependencies
import { useAuth, useUser } from "@clerk/nextjs";
import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getWorkplaces,
  setCurrent,
} from "@store/features/account/workplaceSlice";
import { fetchCommunicationChannels } from "@store/features/crm/crmSlice";
import { fetchThreadsList } from "@store/features/threads/threadSlice";
import { type AppDispatch, type RootState } from "@store/index";
import { useParams } from "next/navigation";
// workplace-level realtime moved to WorkplaceProvider

// Define the UserProvider component
export default function UserProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  // Get the dispatch function from the Redux store
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams<{ subdomain: string | undefined }>();
  const threadsFetchedRef = useRef(false);

  const appState = useSelector(
    (rootState: RootState) => ({
      workplace: rootState.workplace,
    }),
    (prev, next) => {
      return prev.workplace === next.workplace;
    },
  );

  const {
    workplaces: {
      data: workplaces,
      isLoading: isWorkplacesLoading,
      isFetched: isWorkplacesFetched,
    },
    workplace: { data: currentWorkplace },
    currentMember: { data: currentMember },
  } = appState.workplace;

  // Listen for changes to the user using the useUser hook
  const auth = useAuth();
  const { user } = useUser();

  const subdomain = params.subdomain;

  // useEffect is used to dispatch the user data to the Redux store when it changes
  useEffect(() => {
    // Fetch workplaces when user is loaded and we don't have workplaces yet
    if (!workplaces[0] && auth.isSignedIn && user && !isWorkplacesFetched) {
      void dispatch(
        getWorkplaces({
          currentUserId: user.id,
          subdomain: subdomain || "",
        }),
      );
    }
  }, [
    dispatch,
    isWorkplacesLoading,
    auth.isSignedIn,
    isWorkplacesFetched,
    user,
    workplaces,
    subdomain,
  ]);

  // After landing on `/`, workplaces load without a current workplace. Sync when
  // the user navigates into a subdomain route so member-scoped UI (inbox threads)
  // has the correct workplace and member context.
  useEffect(() => {
    if (!subdomain || !user?.id || workplaces.length === 0) return;

    const workplaceForRoute = workplaces.find((w) => w.subdomain === subdomain);
    if (!workplaceForRoute) return;

    const workplaceNeedsSync = currentWorkplace?.subdomain !== subdomain;
    const memberNeedsSync = !currentMember;

    if (!workplaceNeedsSync && !memberNeedsSync) return;

    dispatch(
      setCurrent({
        subdomain,
        currentUserId: user.id,
      }),
    );
  }, [
    dispatch,
    subdomain,
    user?.id,
    workplaces,
    currentWorkplace?.subdomain,
    currentMember,
  ]);

  // Reset the ref when subdomain changes (switching workplaces)
  useEffect(() => {
    threadsFetchedRef.current = false;
  }, [subdomain]);

  // Fetch threads and communication channels when a subdomain route is active
  useEffect(() => {
    if (!subdomain || workplaces.length === 0 || threadsFetchedRef.current) {
      return;
    }

    const workplaceForRoute = workplaces.find((w) => w.subdomain === subdomain);
    if (!workplaceForRoute) return;

    threadsFetchedRef.current = true;
    void dispatch(fetchThreadsList());
    void dispatch(fetchCommunicationChannels());
  }, [dispatch, subdomain, workplaces]);

  // Render the children components
  return <>{children}</>;
}
